import { Box, Divider } from "@chakra-ui/react";
import { useToast } from "@chakra-ui/toast";
import { FC, useCallback } from "react";
import { graphql, useFragment } from "react-relay";
import { useNavigate } from "react-router";

import { LegacyScriptTagSettingTabContainer_Mutation } from "~/src/__relay_artifacts__/LegacyScriptTagSettingTabContainer_Mutation.graphql";
import { LegacyScriptTagSettingTabContainer_site$key } from "~/src/__relay_artifacts__/LegacyScriptTagSettingTabContainer_site.graphql";
import { useMutationCommit } from "~/src/lib/react-relay";

import {
  LegacyScriptTagSettingForm,
  type LegacyScriptTagSettingFormProps,
  RobeeTagCard,
} from "./presentations";

export type Props = {
  siteRef: LegacyScriptTagSettingTabContainer_site$key;
};

const fragment = graphql`
  fragment LegacyScriptTagSettingTabContainer_site on Site {
    id
    embedTag
    robeeTag
  }
`;

const mutation = graphql`
  mutation LegacyScriptTagSettingTabContainer_Mutation(
    $siteId: ID!
    $userTag: String
  ) {
    upsertUserTag(input: { siteId: $siteId, userTag: $userTag }) {
      site {
        id
        embedTag
      }
    }
  }
`;

export const LegacyScriptTagSettingTabContainer: FC<
  React.PropsWithChildren<React.PropsWithChildren<Props>>
> = ({ siteRef }) => {
  const site = useFragment(fragment, siteRef);
  const navigate = useNavigate();
  const toast = useToast();

  const handleCancel = useCallback(() => navigate(-1), [navigate]);

  const mutate =
    useMutationCommit<LegacyScriptTagSettingTabContainer_Mutation>(mutation);

  const handleSubmit = useCallback<LegacyScriptTagSettingFormProps["onSubmit"]>(
    async ({ userTag }) => {
      try {
        const res = await mutate({
          variables: {
            siteId: site.id,
            userTag,
          },
        });
        const upsertedSite = res.upsertUserTag?.site;
        if (!upsertedSite) throw new Error("assertion failed");

        toast({
          title: `javascriptタグを設定しました`,
          status: "success",
        });
      } catch {
        toast({ title: "javascriptタグの設定に失敗しました", status: "error" });
      }
    },
    [mutate, site.id, toast]
  );

  return (
    <Box>
      <RobeeTagCard snippet={site.robeeTag} />
      <Divider mb={16} mt={16} />
      <LegacyScriptTagSettingForm
        initialValues={{
          userTag: site.embedTag,
        }}
        onSubmit={handleSubmit}
        onCancelClick={handleCancel}
      />
    </Box>
  );
};
